/**
 * Google Analytics 4 service for tracking user interactions and page views.
 */

import ReactGA from 'react-ga4';
import { config } from '@/config/env';

/**
 * Initialize Google Analytics 4
 */
export const initializeGA = () => {
  if (!config.gaMeasurementId) {
    console.warn('GA4 Measurement ID not configured');
    return;
  }

  ReactGA.default.initialize(config.gaMeasurementId, {
    // Enable debug mode in development
    gtagOptions: {
      debug_mode: process.env.NODE_ENV === 'development',
    },
    // Only track in production by default
    testMode: process.env.NODE_ENV === 'development',
  });

  console.log('GA4 initialized with ID:', config.gaMeasurementId);
};

/**
 * Track page view
 */
export const trackPageView = (path: string, title?: string) => {
  if (!config.gaMeasurementId) return;

  ReactGA.default.send({
    hitType: 'pageview',
    page: path,
    title: title || document.title,
  });

  console.log('GA4 Page view tracked:', path);
};

/**
 * Track custom events
 */
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number
) => {
  if (!config.gaMeasurementId) return;

  ReactGA.default.event({
    action,
    category,
    label,
    value,
  });

  console.log('GA4 Event tracked:', { action, category, label, value });
};

/**
 * Track user signup
 */
export const trackSignUp = (method: 'email' | 'google' | 'github' = 'email') => {
  trackEvent('sign_up', 'authentication', method);
};

/**
 * Track user login
 */
export const trackLogin = (method: 'email' | 'google' | 'github' = 'email') => {
  trackEvent('login', 'authentication', method);
};

/**
 * Track video creation events
 */
export const trackVideoCreation = {
  start: () => trackEvent('video_creation_start', 'video'),
  complete: (videoId: string) => trackEvent('video_creation_complete', 'video', videoId),
  failed: (error: string) => trackEvent('video_creation_failed', 'video', error),
  retry: (videoId: string) => trackEvent('video_retry', 'video', videoId),
};

/**
 * Track video sharing
 */
export const trackVideoShare = (videoId: string) => {
  trackEvent('share', 'video', videoId);
};

/**
 * Track video downloads
 */
export const trackVideoDownload = (videoId: string) => {
  trackEvent('download', 'video', videoId);
};

/**
 * Track subscription events
 */
export const trackSubscription = {
  planView: (planName: string) => trackEvent('view_plan', 'subscription', planName),
  upgrade: (planName: string) => trackEvent('subscribe', 'subscription', planName),
  cancel: () => trackEvent('cancel_subscription', 'subscription'),
};

/**
 * Track image upload events
 */
export const trackImageUpload = {
  start: (count: number) => trackEvent('image_upload_start', 'media', 'count', count),
  complete: (count: number) => trackEvent('image_upload_complete', 'media', 'count', count),
  failed: (error: string) => trackEvent('image_upload_failed', 'media', error),
};

/**
 * Track navigation events
 */
export const trackNavigation = {
  dashboard: () => trackEvent('navigate_dashboard', 'navigation'),
  videos: () => trackEvent('navigate_videos', 'navigation'),
  account: () => trackEvent('navigate_account', 'navigation'),
  landing: () => trackEvent('navigate_landing', 'navigation'),
};